import _ from 'lodash';
import {
  OrderServicesConstants,
  QuoteFlowerBedWeedingOptions,
  QuoteMulchingOptions,
  QuoteLeafRemovalOptions,
  QuoteCleanupOptions,
} from 'ls-constants';
import { lsApiService } from 'ls-vue-common';

export const formatQuotePayload = ({ pendingQuote }) => {
  const preFormattedPendingQuote = {
    schedules: [
      {
        service_id: pendingQuote.service_id,
        budget: pendingQuote.budget,
        description: formatQuoteDescription({ pendingQuote }),
        additional: _.get(pendingQuote, `additional`, {}),
        ...formatLocationFields({ pendingQuote }),
        ...lsApiService.formatImagesForFormData({
          tag: 'quote.request',
          images: pendingQuote.images,
        }),
      },
    ],
  };

  return lsApiService.formatFormDataPayload({
    payload: preFormattedPendingQuote,
  });
};

export const formatLocationFields = ({ pendingQuote }) => {
  const shortCircuitLocationFieldsMapByServiceId = {
    170: 'bed_weeding',
    6: 'mulching',
  };

  if (shortCircuitLocationFieldsMapByServiceId[pendingQuote.service_id]) {
    return {};
  }

  const locationFields = {};

  _.forEach(OrderServicesConstants.locationsByKey, (location, key) => {
    const selected = _.includes(pendingQuote.locations, key);
    if (selected) {
      _.set(locationFields, key, 1);
    } else {
      _.set(locationFields, key, 0);
    }
  });

  return locationFields;
};

function getDetails({ constant, value }) {
  return _.get(
    _.find(constant, {
      value,
    }),
    'label',
    'not available'
  );
}

export const formatQuoteDescription = ({ pendingQuote }) => {
  const additional = _.get(pendingQuote, 'additional', {});

  const descriptionSections = [];
  const service_id = _.toNumber(pendingQuote.service_id);

  let locationDescription = '';

  if (_.get(pendingQuote, 'locations')) {
    let locations = [];
    _.forEach(OrderServicesConstants.locationsByKey, (location, key) => {
      const selected = _.includes(pendingQuote.locations, key);
      if (selected) {
        locations.push(location);
      }
    });
    locationDescription += locations.join(', ');
  }

  if (locationDescription) {
    descriptionSections.push(`Location (from the street):\n${locationDescription}`);
  }
  if (pendingQuote.itemDetails) {
    descriptionSections.push(`Items needing service:\n${pendingQuote.itemDetails}`);
  }
  if (pendingQuote.desiredWorkDetails) {
    descriptionSections.push(`Desired work to be completed:\n${pendingQuote.desiredWorkDetails}`);
  }

  if (additional.shrub_trim_count) {
    const trimCount = getDetails({
      constant: OrderServicesConstants.bush_trimming.shrub_trim_count,
      value: additional.shrub_trim_count,
    });
    descriptionSections.push(`Number of shrubs / hedges to trim:\n${trimCount}`);
  }

  if (additional.shrub_height) {
    const shrubHeight = getDetails({
      constant: OrderServicesConstants.bush_trimming.shrub_height,
      value: additional.shrub_height,
    });
    descriptionSections.push(`Tallest shrub / hedge:\n${shrubHeight}`);
  }

  if (additional.cut_amount) {
    const cutAmount = getDetails({
      constant: OrderServicesConstants.bush_trimming.cut_amount,
      value: additional.cut_amount,
    });
    descriptionSections.push(
      `Desired amount to be taken off (without damaging the shrubs / hedges):\n${cutAmount}`
    );
  }

  if (additional.desired_handling) {
    const desiredHandling = getDetails({
      constant: QuoteFlowerBedWeedingOptions.desiredHandling,
      value: additional.desired_handling,
    });
    descriptionSections.push(
      `Would you like your crew to cut the weeds with a weed eater and then apply spray or gel?:\n${desiredHandling}`
    );
  }

  // cleanup_spring and cleanup_leaves share a column name, but description is different so explicitly checking service_id
  if (additional.desired_haulaway && service_id !== 10 && service_id !== 7) {
    const desiredHaulaway = getDetails({
      constant: QuoteFlowerBedWeedingOptions.desiredHaulaway,
      value: additional.desired_haulaway,
    });
    descriptionSections.push(
      `Would you like your crew to haul and dispose of the weeds?:\n${desiredHaulaway}`
    );
  }

  if (additional.flower_beds) {
    const formattedFlowerBeds = _.join(
      _.map(additional.flower_beds, (bed, idx) => {
        const isLast = idx === additional.flower_beds.length - 1;
        const size = _.get(
          _.find(QuoteFlowerBedWeedingOptions.sizes, {
            value: bed.size,
          }),
          'label',
          _.replace(bed.size, 'x', ' x ')
        );
        const location = getDetails({
          constant: QuoteFlowerBedWeedingOptions.locations,
          value: bed.location,
        });
        return `#${idx + 1} ${size} ${location}${isLast ? '' : '\n'}`;
      }),
      ''
    );

    descriptionSections.push(
      `What is the size and location of the flower bed(s)?:\n${formattedFlowerBeds}`
    );
  }

  if (additional.desired_sheeting) {
    const desiredSheeting = getDetails({
      constant: QuoteMulchingOptions.desiredSheeting,
      value: additional.desired_sheeting,
    });
    descriptionSections.push(
      `Would you like the crew to put sheeting below mulch?:\n${desiredSheeting}`
    );
  }

  if (additional.desired_type) {
    const desiredType = getDetails({
      constant: QuoteMulchingOptions.desiredType,
      value: additional.desired_type,
    });
    descriptionSections.push(
      `What type of mulch would you like to get for this bed?:\n${desiredType}`
    );
  }

  if (additional.desired_type_other_details) {
    const desiredTypeOtherDetails = _.get(additional, 'desired_type_other_details');

    descriptionSections.push(`Mulch type details:\n${desiredTypeOtherDetails}`);
  }

  if (additional.desired_color) {
    const desiredColor = getDetails({
      constant: QuoteMulchingOptions.desiredColor,
      value: additional.desired_color,
    });
    descriptionSections.push(`What is your preferred mulch color?:\n${desiredColor}`);
  }

  if (_.has(additional, 'remove_mulch')) {
    const removeMulch = getDetails({
      constant: QuoteMulchingOptions.removeMulch,
      value: additional.remove_mulch,
    });
    descriptionSections.push(`Would you like your crew to remove old mulch?:\n${removeMulch}`);
  }

  if (!_.isNil(_.get(additional, 'haul_mulch'))) {
    const haulMulch = getDetails({
      constant: QuoteMulchingOptions.haulMulch,
      value: additional.haul_mulch,
    });
    descriptionSections.push(
      `Would you like your crew to haul & dispose of the old mulch?:\n${haulMulch}`
    );
  }

  if (_.has(additional, 'self_purchase_materials')) {
    const selfPurchaseMaterials = getDetails({
      constant: QuoteMulchingOptions.selfPurchaseMaterials,
      value: additional.self_purchase_materials,
    });
    descriptionSections.push(
      `Will you be purchasing mulch and sheeting material?:\n${selfPurchaseMaterials}`
    );
  }

  // cleanup_leaves
  if (service_id === 10 && !_.isEmpty(additional)) {
    const desired_bed_cleanup = getDetails({
      constant: QuoteLeafRemovalOptions.desired_bed_cleanup,
      value: additional.desired_bed_cleanup,
    });
    descriptionSections.push(
      `Would you also like your crew to remove leaves from flower beds?\n${desired_bed_cleanup}`
    );

    let debrisDescription = `What types of debris needs to be removed?`;
    _.forEach(QuoteLeafRemovalOptions.debrisTypes, ({ value, label }) => {
      if (additional[value]) {
        debrisDescription += `\n- ${value === 'has_other' ? additional.other_debris : label}`;
      }
    });
    descriptionSections.push(debrisDescription);

    const desired_haulaway = getDetails({
      constant: QuoteLeafRemovalOptions.desired_haulaway,
      value: additional.desired_haulaway,
    });
    descriptionSections.push(
      `Would you like your crew to get the debris hauled and disposed?\n${desired_haulaway}`
    );

    const last_service = getDetails({
      constant: QuoteLeafRemovalOptions.last_service,
      value: additional.last_service,
    });
    descriptionSections.push(
      `When was the last time you had leaf removal done at your property?\n${last_service}`
    );
  }

  // cleanup_spring
  if (service_id === 7 && !_.isEmpty(additional)) {
    const desired_bed_cleanup = getDetails({
      constant: QuoteCleanupOptions.desired_bed_cleanup,
      value: additional.desired_bed_cleanup,
    });
    descriptionSections.push(
      `Would you like your crew to also cleanup flower beds?\n${desired_bed_cleanup}`
    );

    let debrisDescription = `What types of debris needs to be removed?`;
    _.forEach(QuoteCleanupOptions.debrisTypes, ({ value, label }) => {
      if (additional[value]) {
        debrisDescription += `\n- ${value === 'has_other' ? additional.other_debris : label}`;
      }
    });
    descriptionSections.push(debrisDescription);

    const desired_haulaway = getDetails({
      constant: QuoteCleanupOptions.desired_haulaway,
      value: additional.desired_haulaway,
    });
    descriptionSections.push(
      `Would you like your crew to get the debris hauled and disposed?\n${desired_haulaway}`
    );

    const last_service = getDetails({
      constant: QuoteCleanupOptions.last_service,
      value: additional.last_service,
    });
    descriptionSections.push(
      `When was the last time you had cleanup done at your property?\n${last_service}`
    );
  }

  if (additional.details) {
    descriptionSections.push(`Additional Details:\n${additional.details}`);
  }

  return _.join(descriptionSections, '\n\n');
};

//eslint-disable-next-line import/prefer-default-export
export const lsOrderServicesStoreHelpers = {
  formatQuotePayload,
  formatQuoteDescription,
  formatLocationFields,
};
